// Lean, dependency-free inline SVG icons. Decorative by default (aria-hidden);
// pass a `title` to make one meaningful to assistive tech.
import type { SVGProps } from 'react';

type Props = SVGProps<SVGSVGElement> & { title?: string };

function Base({ title, children, ...rest }: Props & { children: React.ReactNode }) {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden={title ? undefined : true}
      role={title ? 'img' : undefined}
      focusable="false"
      {...rest}
    >
      {title ? <title>{title}</title> : null}
      {children}
    </svg>
  );
}

export const IconDashboard = (p: Props) => (
  <Base {...p}><rect x="3" y="3" width="7" height="9" rx="1.5" /><rect x="14" y="3" width="7" height="5" rx="1.5" /><rect x="14" y="12" width="7" height="9" rx="1.5" /><rect x="3" y="16" width="7" height="5" rx="1.5" /></Base>
);
export const IconArchive = (p: Props) => (
  <Base {...p}><rect x="3" y="4" width="18" height="4" rx="1" /><path d="M5 8v11a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1V8" /><path d="M10 12h4" /></Base>
);
export const IconUpload = (p: Props) => (
  <Base {...p}><path d="M12 15V3" /><path d="m7 8 5-5 5 5" /><path d="M4 15v4a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-4" /></Base>
);
export const IconCloud = (p: Props) => (
  <Base {...p}><path d="M17.5 19a4.5 4.5 0 0 0 .5-8.98A6 6 0 0 0 6.2 9.3 4 4 0 0 0 7 19z" /><path d="M12 12v6" /><path d="m9.5 14.5 2.5-2.5 2.5 2.5" /></Base>
);
export const IconSearch = (p: Props) => (
  <Base {...p}><circle cx="11" cy="11" r="7" /><path d="m21 21-4.3-4.3" /></Base>
);
export const IconVersions = (p: Props) => (
  <Base {...p}><path d="M3 3v6h6" /><path d="M3.5 9a9 9 0 1 0 2.1-3.4L3 9" /><path d="M12 8v4l3 2" /></Base>
);
export const IconRetention = (p: Props) => (
  <Base {...p}><path d="M12 22a10 10 0 1 0-7-2.9" /><path d="M12 6v6l4 2" /><path d="M3 12H1m2-3H1" /></Base>
);
export const IconRetrieval = (p: Props) => (
  <Base {...p}><path d="M12 3v12" /><path d="m7 10 5 5 5-5" /><path d="M4 21h16" /></Base>
);
export const IconAudit = (p: Props) => (
  <Base {...p}><path d="M14 3v4a1 1 0 0 0 1 1h4" /><path d="M17 21H7a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h7l5 5v11a2 2 0 0 1-2 2Z" /><path d="m9 14 2 2 4-4" /></Base>
);
export const IconBilling = (p: Props) => (
  <Base {...p}><rect x="2" y="5" width="20" height="14" rx="2" /><path d="M2 10h20" /><path d="M6 15h4" /></Base>
);
export const IconBell = (p: Props) => (
  <Base {...p}><path d="M18 8a6 6 0 0 0-12 0c0 7-3 9-3 9h18s-3-2-3-9" /><path d="M13.7 21a2 2 0 0 1-3.4 0" /></Base>
);
export const IconLogout = (p: Props) => (
  <Base {...p}><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" /><path d="m16 17 5-5-5-5" /><path d="M21 12H9" /></Base>
);
export const IconMenu = (p: Props) => (
  <Base {...p}><path d="M4 6h16M4 12h16M4 18h16" /></Base>
);
export const IconGlobe = (p: Props) => (
  <Base {...p}><circle cx="12" cy="12" r="9" /><path d="M3 12h18" /><path d="M12 3a15 15 0 0 1 0 18 15 15 0 0 1 0-18Z" /></Base>
);
export const IconSun = (p: Props) => (
  <Base {...p}><circle cx="12" cy="12" r="4" /><path d="M12 2v2m0 16v2M4.9 4.9l1.4 1.4m11.4 11.4 1.4 1.4M2 12h2m16 0h2M4.9 19.1l1.4-1.4M17.7 6.3l1.4-1.4" /></Base>
);
export const IconMoon = (p: Props) => (
  <Base {...p}><path d="M21 12.8A9 9 0 1 1 11.2 3a7 7 0 0 0 9.8 9.8Z" /></Base>
);
export const IconWarning = (p: Props) => (
  <Base {...p}><path d="M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0Z" /><path d="M12 9v4" /><path d="M12 17h.01" /></Base>
);
export const IconError = (p: Props) => (
  <Base {...p}><circle cx="12" cy="12" r="9" /><path d="m15 9-6 6m0-6 6 6" /></Base>
);
export const IconInfo = (p: Props) => (
  <Base {...p}><circle cx="12" cy="12" r="9" /><path d="M12 16v-4m0-4h.01" /></Base>
);
export const IconCheck = (p: Props) => (
  <Base {...p}><path d="M20 6 9 17l-5-5" /></Base>
);
export const IconPlus = (p: Props) => (
  <Base {...p}><path d="M12 5v14M5 12h14" /></Base>
);
export const IconDownload = (p: Props) => (
  <Base {...p}><path d="M12 3v12" /><path d="m7 10 5 5 5-5" /><path d="M5 21h14" /></Base>
);
export const IconInbox = (p: Props) => (
  <Base {...p}><path d="M22 12h-6l-2 3h-4l-2-3H2" /><path d="M5.5 5.5 2 12v6a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-6l-3.5-6.5A2 2 0 0 0 16.8 4H7.2a2 2 0 0 0-1.7 1.5Z" /></Base>
);
export const IconClose = (p: Props) => (
  <Base {...p}><path d="M18 6 6 18M6 6l12 12" /></Base>
);
export const IconCopy = (p: Props) => (
  <Base {...p}><rect x="9" y="9" width="12" height="12" rx="2" /><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" /></Base>
);
export const IconChevronRight = (p: Props) => (
  <Base {...p}><path d="m9 18 6-6-6-6" /></Base>
);
export const IconTrash = (p: Props) => (
  <Base {...p}><path d="M3 6h18" /><path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" /><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" /><path d="M10 11v6M14 11v6" /></Base>
);
export const IconRefresh = (p: Props) => (
  <Base {...p}><path d="M21 12a9 9 0 1 1-2.64-6.36" /><path d="M21 3v6h-6" /></Base>
);
export const IconUsers = (p: Props) => (
  <Base {...p}><path d="M17 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M23 21v-2a4 4 0 0 0-3-3.87" /><path d="M16 3.13a4 4 0 0 1 0 7.75" /></Base>
);
export const IconLayers = (p: Props) => (
  <Base {...p}><path d="m12 2 9 5-9 5-9-5 9-5Z" /><path d="m3 12 9 5 9-5" /><path d="m3 17 9 5 9-5" /></Base>
);
export const IconEye = (p: Props) => (
  <Base {...p}><path d="M1 12s4-7 11-7 11 7 11 7-4 7-11 7-11-7-11-7Z" /><circle cx="12" cy="12" r="3" /></Base>
);
